import { CountryModel } from '../localization-and-pricing/country.model';
import { CultureModel } from '../localization-and-pricing/culture.model';
import { StudentModel } from '../authentication/student.model';
import {StringExtensionModel} from '../extensions/string-extension.model';
import {DropdownModel} from '../component/dropdown.model';
import { SchoolListItem } from './school-list-item.interface';

export class SchoolModel implements SchoolListItem {

  static currentSchoolKey = 'currentSchoolKey';
  static currentSchool: SchoolModel = null;

  static MultiplicationSign = {
    xSign: 'xSign',
    dotSign: 'dotSign',
    asteriskSign: 'asteriskSign',
    list: function() {
      return [
        this.xSign,
        this.dotSign,
        this.asteriskSign
      ];
    },
    dropdownModel: function() {
      return [
        new DropdownModel(this.xSign, `multiplicationSign.${this.xSign}`),
        new DropdownModel(this.dotSign, `multiplicationSign.${this.dotSign}`),
        new DropdownModel(this.asteriskSign, `multiplicationSign.${this.asteriskSign}`)
      ];
    }
  };

  static DivisionSign = {
    slashSign: 'slashSign',
    colonSign: 'colonSign',
    standardSign: 'standardSign',
    list: function() {
      return [
        this.slashSign,
        this.colonSign,
        this.standardSign
      ];
    },
    dropdownModel: function() {
      return [
        new DropdownModel(this.slashSign, `divisionSign.${this.slashSign}`),
        new DropdownModel(this.colonSign, `divisionSign.${this.colonSign}`),
        new DropdownModel(this.standardSign, `divisionSign.${this.standardSign}`)
      ];
    }
  };

  static UnitSystem = {
    metricSystem: 'metricSystem',
    imperialSystem: 'imperialSystem', // Currently not in use was dropped by the UK
    usCustomarySystem: 'usCustomarySystem',
    list: function() {
      return [
        this.metricSystem,
        this.usCustomarySystem
      ];
    },
    dropdownModel: function() {
      return [
        new DropdownModel(this.metricSystem, `unitSystem.${this.metricSystem}`),
        new DropdownModel(this.usCustomarySystem, `unitSystem.${this.usCustomarySystem}`)
      ];
    }
  };

  _id: string;
  name: string;
  address: string;
  city: string;
  zip: string;
  schoolType: string;
  country: string;
  culture: string;
  numLicenses: number;
  pricePerStudent: number;
  currency: string;
  licenseStartsAt: Date;
  licenseExpiresAt: Date;
  createdAt: Date;
  updatedAt: Date;
  licenseUpdatedAt: Date;

  organizationId: string;
  organizationName: string;

  linkInvitationEnabled: boolean;
  linkInvitationId: string;

  excludeFromBusinessMetrics: boolean;
  isTestSchool: Boolean;
  isTeacherTestSchool: Boolean;

  localeSettings = {
    multiplicationSign: SchoolModel.MultiplicationSign.xSign,
    divisionSign: SchoolModel.DivisionSign.standardSign,
    unitSystem: SchoolModel.UnitSystem.metricSystem
  };

  constructor() {
    this.country = CountryModel.ISL;
    this.culture = CultureModel.isIS;
  }

  static generate(json: any): SchoolModel {

    const school = new SchoolModel();
    school._id = json._id;
    school.name = json.name;
    school.address = json.address;
    school.city = json.city;
    school.zip = json.zip;
    school.country = json.country;
    school.numLicenses = json.numLicenses;
    school.pricePerStudent = json.pricePerStudent;
    school.currency = json.currency;
    school.culture = json.culture;
    school.schoolType = json.schoolType;
    school.linkInvitationEnabled = json.linkInvitationEnabled;
    school.linkInvitationId = json.linkInvitationId;
    school.excludeFromBusinessMetrics = json.excludeFromBusinessMetrics;
    school.isTestSchool = json.isTestSchool;

    school.isTeacherTestSchool = json.isTeacherTestSchool;

    school.organizationId = json.organizationId || null;
    school.organizationName = json.organizationName || null;

    if (school.linkInvitationEnabled === undefined || school.linkInvitationEnabled === null) {
      school.linkInvitationEnabled = false;
    }

    if (school.schoolType === null) {
      school.schoolType = StudentModel.SchoolTypes.public;
    }

    if (json.licenseExpiresAt) {
      school.licenseExpiresAt = new Date(json.licenseExpiresAt);
    }

    if (json.licenseStartsAt) {
      school.licenseStartsAt = new Date(json.licenseStartsAt);
    }

    if (json.createdAt) {
      school.createdAt = new Date(json.createdAt);
    }
    if (json.updatedAt) {
      school.updatedAt = new Date(json.updatedAt);
    }
    if (json.licenseUpdatedAt) {
      school.licenseUpdatedAt = new Date(json.licenseUpdatedAt);
    }

    if (json.localeSettings) {
      school.localeSettings.multiplicationSign = json.localeSettings.multiplicationSign;
      school.localeSettings.divisionSign = json.localeSettings.divisionSign;
      school.localeSettings.unitSystem = json.localeSettings.unitSystem;
    }

    return school;

  }

  static generateList(jsonList: any[]): SchoolModel[] {

    if (!jsonList || !Array.isArray(jsonList)) {
      return [];
    }

    const list = [];

    for (const json of jsonList) {
      const school = SchoolModel.generate(json);
      list.push(school);
    }

    return list;

  }

  static getCurrent(): SchoolModel {

    if (this.currentSchool) {
      return this.currentSchool;
    }

    const schoolJson = localStorage.getItem(SchoolModel.currentSchoolKey);
    if (schoolJson) {
      this.currentSchool = SchoolModel.generate(JSON.parse(schoolJson));
    }

    return this.currentSchool;
  }

  static setCurrent(school: SchoolModel) {

    this.currentSchool = school;

    if (this.currentSchool) {
      localStorage.setItem(this.currentSchoolKey, JSON.stringify(this.currentSchool));
    } else {
      this.removeCurrent();
    }

  }

  static removeCurrent() {
    this.currentSchool = null;
    localStorage.removeItem(this.currentSchoolKey);
  }

  getTwoInitials() {

    return StringExtensionModel.getInitials(this.name, 2);

  }

  getSchoolName(): string {
    return this.name;
  }

  getSchoolCity(): string {
    return this.city;
  }

}

export class SchoolFilter {

  skip = 0;
  schoolIds: string;
  excludeSchoolIds: string;
  limit: number = undefined;
  country: string = undefined;
  search: string = undefined;
  // send 1 to include teacher schools
  includeTeacherSchools: number = null;
  // send 1 to restrict to schools without an organizationId
  withoutOrganizationId: number = null;

  sort = 1;

}
